import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout, logoutUser, lockScreen } from '../store/authSlice';
import { setLanguage } from '../store/langSlice';
import { fetchFunnels } from '../store/funnelSlice';
import { t, useT } from '../utils/translate';
import { mediaUrl } from '../utils/media';
import { usePermissions } from '../utils/permissions';
import { NAV_ITEMS } from '../config/navItems';
import GlobalSearch from './GlobalSearch';
import { useAppearance } from '../utils/appearance';
import NotificationBell from './NotificationCenter';
import { useHotkeys } from '../utils/hotkeys';
import {
  Building2, Settings, LogOut, UserCircle, ChevronDown, Lock,
  Maximize, Minimize, RotateCcw, Kanban, PanelTopClose, Search, Plus, Users, Sun, Moon, Monitor,
} from 'lucide-react';

function OrgBrand() {
  const org = useSelector((s) => s.auth.user?.organization);
  const navigate = useNavigate();
  const brandName = org?.name || 'CRM Line';
  return (
    <button onClick={() => navigate('/dashboard')}
      className="flex items-center gap-2.5 min-w-0 rounded-lg hover:bg-white/10 transition-colors -mx-1 px-1 py-0.5">
      <div className="w-8 h-8 bg-white/15 rounded-xl flex items-center justify-center overflow-hidden shrink-0">
        {org?.logo
          ? <img src={mediaUrl(org.logo)} alt={brandName} className="w-full h-full object-cover" />
          : <Building2 className="w-4 h-4 text-white" />}
      </div>
      <span className="font-semibold text-white text-sm leading-none truncate max-w-[110px] sm:max-w-[220px]">{brandName}</span>
    </button>
  );
}

// Пункт верхнего меню: по умолчанию только иконка (экономит место), по наведению или фокусу плавно раскрывается название.
// Название всегда доступно скринридеру (aria-label) и в подсказке (title).
function NavPill({ icon, label, active, onClick, trailing, menuOpen }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-current={active ? 'page' : undefined}
      aria-haspopup={menuOpen !== undefined ? 'menu' : undefined}
      aria-expanded={menuOpen}
      className={`group flex items-center h-9 px-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${
        active ? 'bg-white/15 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'
      }`}
    >
      {icon}
      <span className="overflow-hidden max-w-0 opacity-0 ml-0 transition-all duration-200 ease-out group-hover:max-w-[200px] group-hover:opacity-100 group-hover:ml-2 group-focus-visible:max-w-[200px] group-focus-visible:opacity-100 group-focus-visible:ml-2">
        {label}
      </span>
      {trailing}
    </button>
  );
}

// У воронок общая иконка не различает их, поэтому в свёрнутом виде это первые буквы названия
function FunnelBadge({ name }) {
  const letters = String(name || '?').trim().slice(0, 2);
  return (
    <span className="w-[22px] h-[22px] rounded-md bg-black/20 text-white text-xs font-bold leading-none flex items-center justify-center shrink-0 uppercase">
      {letters}
    </span>
  );
}

function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

// Меню «Воронки»: все воронки организации в одном выпадающем списке (текущая отмечена)
function FunnelsMenu({ funnels, active, onNavigate }) {
  const tFn = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));
  const current = funnels.find(f => active === `funnel-${f._id}`);
  return (
    <div ref={ref} className="relative">
      <NavPill
        active={!!current}
        label={current ? current.name : tFn('topbar.funnels')}
        icon={<Kanban className="w-[18px] h-[18px] shrink-0" />}
        onClick={() => setOpen(v => !v)}
        menuOpen={open}
        trailing={<ChevronDown className={`w-3.5 h-3.5 shrink-0 ml-0.5 transition-transform ${open ? 'rotate-180' : ''}`} />}
      />
      {open && (
        <div role="menu" className="absolute left-0 top-full mt-1.5 min-w-[220px] max-h-[60vh] overflow-y-auto bg-white border border-surface-200 rounded-xl shadow-modal py-1 z-50">
          {funnels.map(f => {
            const isCurrent = active === `funnel-${f._id}`;
            return (
              <button
                key={f._id}
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); onNavigate?.(`funnel-${f._id}`); }}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors ${isCurrent ? 'bg-primary-50 text-primary-800 font-medium' : 'text-ink hover:bg-surface-50'}`}
              >
                <Kanban className="w-4 h-4 text-ink-tertiary shrink-0" />
                <span className="truncate">{f.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Кнопка «+ Создать»: быстрые действия из любого раздела
function CreateMenu({ active }) {
  const tFn = useT();
  const navigate = useNavigate();
  const perm = usePermissions();
  const funnels = useSelector((s) => s.funnels.list);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));
  // Сделка создаётся в текущей воронке (если открыта) или в первой
  const targetFunnel = funnels.find(f => active === `funnel-${f._id}`) || funnels[0];
  const actions = [
    perm.canSeeModule('funnels') && perm.can('funnels', 'create') && targetFunnel && { key: 'deal', icon: Kanban, label: tFn('topbar.createDeal'), to: `/funnel/${targetFunnel._id}/deal/new` },
    perm.canSeeModule('contacts') && perm.can('contacts', 'create') && { key: 'contact', icon: Users, label: tFn('topbar.createContact'), to: '/contacts/new' },
  ].filter(Boolean);
  if (!actions.length) return null;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white text-primary-800 text-sm font-semibold hover:bg-white/90 transition-colors"
      >
        <Plus className="w-4 h-4" />
        <span className="hidden sm:inline">{tFn('topbar.create')}</span>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-1.5 w-52 bg-white border border-surface-200 rounded-xl shadow-modal py-1 z-50">
          {actions.map(a => (
            <MenuItem key={a.key} icon={a.icon} label={a.label} onClick={() => { setOpen(false); navigate(a.to); }} />
          ))}
        </div>
      )}
    </div>
  );
}

function NavBar({ active, onNavigate }) {
  const tFn = useT();
  const dispatch = useDispatch();
  const { canSeeModule } = usePermissions();
  const isAuthed = useSelector((s) => s.auth.isAuthenticated);
  const funnels  = useSelector((s) => s.funnels.list);
  const fLoaded  = useSelector((s) => s.funnels.loaded);

  useEffect(() => {
    if (isAuthed && !fLoaded) dispatch(fetchFunnels());
  }, [isAuthed, fLoaded, dispatch]);

  // «Главная» убрана из верхнего меню: туда ведёт кнопка с названием организации слева
  const navItems = NAV_ITEMS.filter(i => i.key !== 'dashboard' && canSeeModule(i.key));

  return (
    <nav className="hidden lg:flex items-center gap-0.5">
      {navItems.map(item => (
        <NavPill
          key={item.key}
          active={active === item.key}
          label={tFn(item.labelKey)}
          icon={<item.icon className="w-[18px] h-[18px] shrink-0" />}
          onClick={() => onNavigate?.(item.key)}
        />
      ))}

      {/* 3 и более воронок: одно меню «Воронки», иначе панель переполняется */}
      {funnels.length >= 3 && (
        <>
          <div className="w-px h-4 bg-white/20 mx-1" />
          <FunnelsMenu funnels={funnels} active={active} onNavigate={onNavigate} />
        </>
      )}
      {funnels.length > 0 && funnels.length < 3 && (
        <>
          <div className="w-px h-4 bg-white/20 mx-1" />
          {funnels.map(f => (
            <NavPill
              key={f._id}
              active={active === `funnel-${f._id}`}
              label={f.name}
              icon={<FunnelBadge name={f.name} />}
              onClick={() => onNavigate?.(`funnel-${f._id}`)}
            />
          ))}
        </>
      )}
    </nav>
  );
}

const LANGS = [
  { code: 'uz',      short: 'UZ',  label: "O'zbek"  },
  { code: 'ru',      short: 'RU',  label: 'Русский' },
  { code: 'en',      short: 'EN',  label: 'English' },
  { code: 'tr',      short: 'TR',  label: 'Türkçe'  },
];

function MenuItem({ icon: Icon, label, onClick, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
        danger ? 'text-red-600 hover:bg-red-50' : 'text-ink hover:bg-surface-50'
      }`}
    >
      <Icon className={`w-4 h-4 ${danger ? '' : 'text-ink-tertiary'}`} />
      {label}
    </button>
  );
}

// Единое меню профиля: аккаунт, язык и все «редкие» действия, которые раньше
// занимали место в шапке на каждой странице.
export function UserDropdown({ onAccountSettings, onCollapse }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lang = useSelector((s) => s.lang.current);
  const { user } = useSelector((s) => s.auth);
  const [open, setOpen] = useState(false);
  const [isFs, setIsFs] = useState(Boolean(document.fullscreenElement));
  const ref = useRef(null);
  const appearance = useAppearance();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onChange = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const run = (fn) => () => { setOpen(false); fn?.(); };
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.();
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors"
      >
        <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center shrink-0">
          <span className="text-white font-semibold text-xs">
            {user?.name?.charAt(0)?.toUpperCase()}
          </span>
        </div>
        <div className="hidden xl:block text-left">
          <p className="text-white text-xs font-medium leading-none whitespace-nowrap">{user?.name}</p>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-60 bg-white border border-surface-200 rounded-xl shadow-modal py-1 z-50">
          <div className="px-4 py-3 border-b border-surface-100">
            <p className="text-sm font-semibold text-ink">{user?.name}</p>
            <p className="text-xs text-ink-tertiary mt-0.5">{user?.phone}</p>
          </div>

          {/* Язык */}
          <div className="px-4 py-3 border-b border-surface-100">
            <p className="text-xs font-semibold text-ink-tertiary uppercase tracking-wide mb-2">{t('topbar.language', lang)}</p>
            <div className="grid grid-cols-4 gap-1 p-0.5 bg-surface-100 rounded-lg">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  title={l.label}
                  onClick={() => dispatch(setLanguage(l.code))}
                  className={`py-1 text-xs font-medium rounded-md transition-colors ${
                    lang === l.code ? 'bg-white text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'
                  }`}
                >
                  {l.short}
                </button>
              ))}
            </div>
          </div>

          {/* Оформление: тема и упрощённый вид */}
          <div className="px-4 py-3 border-b border-surface-100">
            <p className="text-xs font-semibold text-ink-tertiary uppercase tracking-wide mb-2">{t('topbar.appearance', lang)}</p>
            <div className="grid grid-cols-3 gap-1 p-0.5 bg-surface-100 rounded-lg" role="radiogroup" aria-label={t('topbar.appearance', lang)}>
              {[['light', Sun, 'topbar.themeLight'], ['dark', Moon, 'topbar.themeDark'], ['system', Monitor, 'topbar.themeAuto']].map(([mode, Icon, key]) => (
                <button key={mode} type="button" role="radio" aria-checked={appearance.mode === mode} onClick={() => appearance.setThemeMode(mode)}
                  className={`flex items-center justify-center gap-1 py-1.5 text-xs font-medium rounded-md transition-colors ${appearance.mode === mode ? 'bg-card text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'}`}>
                  <Icon className="w-3.5 h-3.5" />{t(key, lang)}
                </button>
              ))}
            </div>
            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <input type="checkbox" checked={appearance.lite} onChange={(e) => appearance.setLite(e.target.checked)} className="mt-0.5 rounded border-surface-200 text-primary-600 focus:ring-primary-500" />
              <span><span className="block text-sm text-ink">{t('topbar.liteMode', lang)}</span><span className="block text-xs text-ink-tertiary">{t('topbar.liteHint', lang)}</span></span>
            </label>
          </div>

          <div className="py-1">
            <MenuItem icon={UserCircle} label={t('topbar.accountSettings', lang)} onClick={run(onAccountSettings)} />
            {['owner', 'admin'].includes(user?.role) && (
              <MenuItem icon={Settings} label={t('topbar.settings', lang)} onClick={run(() => navigate('/settings'))} />
            )}
          </div>

          <div className="border-t border-surface-100 py-1">
            <MenuItem icon={Lock} label={t('topbar.lockScreen', lang)} onClick={run(() => dispatch(lockScreen()))} />
            <MenuItem
              icon={isFs ? Minimize : Maximize}
              label={t(isFs ? 'topbar.exitFullscreen' : 'topbar.fullscreen', lang)}
              onClick={run(toggleFullscreen)}
            />
            <MenuItem icon={RotateCcw} label={t('topbar.refresh', lang)} onClick={run(() => window.location.reload())} />
            <MenuItem icon={PanelTopClose} label={t('topbar.hidePanel', lang)} onClick={run(onCollapse)} />
          </div>

          <div className="border-t border-surface-100 py-1">
            <MenuItem
              icon={LogOut}
              label={t('logout', lang)}
              danger
              onClick={async () => { setOpen(false); await dispatch(logoutUser()); dispatch(logout()); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function TopBar({ left, right, onAccountSettings, active, onNavigate, showNav = true }) {
  const lang = useSelector((s) => s.lang.current);
  const [collapsed, setCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Горячие клавиши: / — поиск, N — новая сделка, G + буква — переход по разделам
  const openSearch = useCallback(() => setSearchOpen(true), []);
  useHotkeys({ onSearch: openSearch });

  // Ctrl/⌘+K открывает глобальный поиск из любого раздела
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearchOpen(v => !v); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        title={t('topbar.showPanel', lang)}
        className="w-full bg-primary-800 flex items-center justify-center py-1 safe-top shrink-0"
      >
        <ChevronDown className="w-4 h-4 text-white/60" />
      </button>
    );
  }

  return (
    <>
      <header className="bg-primary-800 px-4 lg:px-5 py-2.5 flex items-center gap-3 shrink-0 safe-top [--safe-pad:0.625rem]">
        {/* Left: mobile menu + logo */}
        <div className="flex items-center gap-3 min-w-0">
          {left}
          <OrgBrand />
        </div>

        {/* Center: nav items (desktop) */}
        {showNav && (
          <div className="hidden lg:flex items-center gap-1 mx-3">
            <div className="w-px h-5 bg-white/20 mr-2" />
            <NavBar active={active} onNavigate={onNavigate} />
          </div>
        )}

        {/* Right: слот страницы + меню профиля (язык, обновление, экран, блокировка — внутри меню) */}
        <div className="flex items-center gap-2 ml-auto">
          {right && <>{right}<div className="w-px h-5 bg-white/20" /></>}
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            title={`${t('topbar.search', lang)} (Ctrl+K)`}
            aria-label={t('topbar.search', lang)}
            className="flex items-center gap-2 h-8 px-2.5 rounded-lg text-white/75 hover:text-white hover:bg-white/10 transition-colors"
          >
            <Search className="w-4 h-4" />
            <span className="hidden 2xl:inline text-sm">{t('topbar.search', lang)}</span>
            <kbd className="hidden 2xl:inline whitespace-nowrap text-xs text-white/60 border border-white/25 rounded px-1">Ctrl K</kbd>
          </button>
          <NotificationBell />
          <CreateMenu active={active} />
          <UserDropdown onAccountSettings={onAccountSettings} onCollapse={() => setCollapsed(true)} />
        </div>
      </header>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <div className="h-px bg-primary-600/30 shrink-0" />
    </>
  );
}
